import { AccountingData, AccountingPeriodClosingOption, AccountingReportTable, AccountingReportTableType, AccountingRowReportData, ApiValidation, Calculation, CalculationReportData, CalculationStatus, CumulativeCalculation, PeriodDateKind, PeriodType, Report, ReportType, SummaryReportData, WorkerSummaryReportData, AccountingHolidayPayDebtReportData, Language,PayStatsCalculationReportData } from "../model";
import { Dates } from "../util";
import { Ajax } from "./infra";
import { ReportsLogic, Configs } from "../logic";

/**
 * Report types that are available for calculations as stand-alone (typically pop-up dialog)
 */
export type calcReportType = "salarySlip" | "employerReport" | "paymentReport" | "paymentSummaryReport" | "accountingReport" | "totalsReport" | "rowsReport" | "irRows";

/**
 * Report partials that are available as HTML for developers to create their own
 * reporting views
 */
export type reportPartial = "employer" | "employerPage2" | "worker" | "workerPage2" | "payments";

/**
 * Provides access to HTML reports on calculations
 */
export class Reports {

  /**
   * For NG1-dependency injection
   * @ignore
   */
  public static $inject = ["AjaxNg1", "$http"];

  constructor(private ajax: Ajax) { }

  /**
   * Gets a list of generated HTML/PDF/JSON reports by type.
   *
   * @param reportType - Type of reports to fetch.
   *
   * @returns A Promise with result data (array of reports).
   */
  public getReportsByType(reportType: ReportType): Promise<Report[]> {
    return this.ajax.getJSON("/reports/type/" + reportType);
  }

  /**
   * Gets a single generated HTML/PDF/JSON report by type and id.
   *
   * @param reportType - Type of the report to fetch.
   * @param id - Id of the report to fetch.
   * @param wait - Optional flag to indicate whether to wait for the report to be generated.
   *
   * @returns A Promise with result data (report).
   */
  public getReport(reportType: ReportType, id: string, wait: boolean): Promise<Report> {
    const method = `/reports/type/${reportType}/encodeURIComponent(id)?wait=${wait}`;
    return this.ajax.getJSON(method);
  }

  /**
   * Gets the monthly / quarterly/ yearly accounting data for the current account.
   *
   * @param refDate Reference date for the period. Please note that even if the date is not the first day of the given period, the entire period is returned.
   * @param target Accounting target name : contains  default ruleset and other parameters for creating accounting entries.
   * @param periodType Month, quarter, year or a custom period. The custom period requires endDate. Default value is the month.
   * @param endDate End date for the period. Required only for the custom period.
   * @param periodDateKind Period date type: paid at date or salary date.
   * @param minStatus Minimum status level for the calculations to include in the data. The default level is PaymentSucceeded.
   * @param ruleSet Optional additional ruleset for the target to use for creating the accounting entries.
   * @param createdFrom Optional minimum creation date for the calculation included in the report.
   * @returns A Promise with result data (Raw data for accounting purposes).
   */
  public getAccountingDataForPeriod(refDate: string, target?: string, periodType?: PeriodType, endDate?: string, periodDateKind?: PeriodDateKind, minStatus?: CalculationStatus, ruleSet?: string, createdFrom?: string): Promise<AccountingData> {
    refDate = Dates.asDate(refDate) || Dates.getToday();
    const prmsArr: string[] = [];
    if (target) {
      prmsArr.push(`target=${encodeURIComponent(target)}`);
    }
    if (ruleSet) {
      prmsArr.push(`ruleSet=${encodeURIComponent(ruleSet)}`);
    }
    if (periodType) {
      prmsArr.push(`periodType=${periodType}`);
    }
    if (endDate) {
      prmsArr.push(`endDate=${endDate}`);
    }
    if (minStatus) {
      prmsArr.push(`minStatus=${minStatus}`);
    }
    if (periodDateKind) {
      prmsArr.push(`periodDateKind=${periodDateKind}`);
    }
    if (createdFrom) {
      prmsArr.push(`createdFrom=${createdFrom}`);
    }
    const prms = prmsArr.join("&");

    return this.ajax.getJSON(`/v03/api/reports/accounting/${refDate}${prms ? "?" + prms : ""}`);
  }

  /**
   * Gets the accounting data based on given set of calculations.
   *
   * @param calculationIds - Calculations that are the bases for the report.
   * @param target  - Accounting target name : contains  default ruleset and other parameters for creating accounting entries.
   * @param ruleSet - Optional additional ruleset for the target to use for creating the accounting entries.
   * @returns A Promise with result data (Raw data for accounting purposes).
   */
  public getAccountingDataForCalculationIds(calculationIds: string[], target?: string, ruleSet?: string): Promise<AccountingData> {
    const ids = calculationIds.map((x) => `calculationIds=${x}`).join("&");
    const prmsArr: string[] = [];
    if (target) {
      prmsArr.push(`target=${encodeURIComponent(target)}`);
    }
    if (ruleSet) {
      prmsArr.push(`ruleSet=${encodeURIComponent(ruleSet)}`);
    }
    const prms = prmsArr.join("&");
    return this.ajax.getJSON(`/v03/api/reports/accounting?${ids}${prms ? "&" + prms : ""}`);
  }

  /**
   * Gets the accounting data based on given report id.
   *
   * @param id - Saved report id containing accounting data.
   * @param target  - Accounting target name : contains  default ruleset and other parameters for creating accounting entries.
   * @param ruleSet - Optional additional ruleset for the target to use for creating the accounting entries.
   * @returns A Promise with result data (Raw data for accounting purposes).
   */
  public getAccountingData(id: string, target?: string, ruleSet?: string): Promise<AccountingData> {
    const prmsArr: string[] = [];
    if (target) {
      prmsArr.push(`target=${encodeURIComponent(target)}`);
    }
    if (ruleSet) {
      prmsArr.push(`ruleSet=${encodeURIComponent(ruleSet)}`);
    }
    const prms = prmsArr.join("&");
    return this.ajax.getJSON(`/v03/api/reports/accounting/${id}${prms ? "?" + prms : ""}`);
  }

  /**
   * Gets the accounting report based on given set of calculations.
   * This method can be used anonymously.
   *
   * @param calculations - Calculations that are the bases for the report.
   * @param target  - Accounting target name : contains  default ruleset and other parameters for creating accounting entries.
   * @param ruleSet - Optional additional ruleset for the target to use for creating the accounting entries.
   * @returns A Promise with result data (Raw data for accounting purposes).
   */
  public getAccountingDataForCalculations(calculations: Calculation[], target?: string, ruleSet?: string): Promise<AccountingData> {
    const prmsArr: string[] = [];
    if (target) {
      prmsArr.push(`target=${encodeURIComponent(target)}`);
    }
    if (ruleSet) {
      prmsArr.push(`ruleSet=${encodeURIComponent(ruleSet)}`);
    }
    const prms = prmsArr.join("&");
    return this.ajax.postJSON(`/v03/api/reports/accounting${prms ? "?" + prms : ""}`, calculations);
  }

  /**
   * Closes the accounting period for the given period, and generates a report for the period.
   * @param refDate - Period reference date.
   * @param option - Closing option. Currently only default -option supported.
   * @returns Report id for the period.
   */
  public closeAccountingPeriod(refDate: string, option?: AccountingPeriodClosingOption): Promise<string> {
    return this.ajax.postJSON(`/v03/api/reports/accounting/${refDate}/close${option ? "?option=" + option : ""}`, null);
  }

  /**
   * Gets the accounting data for the holiday pay debt.
   *
   * @param refDate The date to which the holiday pay debt is calculated.
   * @param target Accounting target name : contains  default ruleset and other parameters for creating accounting entries.
   * @param employmentIds Optional list of employment ids (by official id) to include in the report.
   * @param fromHolidayYear Optional starting holiday year for the calculations.PaymentSucceeded.
   * @returns A Promise with result data. Holiday pay debt accounting data.
   */
  public getAccountingDataForHolidayPayDebt(refDate: string, target?: string, employmentIds?: string[], fromHolidayYear?: string): Promise<AccountingData> {
    refDate = Dates.asDate(refDate) || Dates.getToday();
    const prmsArr: string[] = [];
    if (target) {
      prmsArr.push(`target=${encodeURIComponent(target)}`);
    }
    if (employmentIds) {
      employmentIds.forEach((x) => prmsArr.push(`employmentIds=${x}`));
    }
    if (fromHolidayYear) {
      prmsArr.push(`fromHolidayYear=${fromHolidayYear}`);
    }
    const prms = prmsArr.join("&");

    return this.ajax.getJSON(`/v03/api/reports/accounting/${refDate}/holiday-pay-debt${prms ? "?" + prms : ""}`);
  }

  /**
   * Gets the monthly / quarterly/ yearly summary data for the current account.
   *
   * @param refDate Reference date for the period. Please note that even if the date is not the first day of the given period, the entire period is returned.
   * @param periodType Month, quarter, year or a custom period. The custom period requires endDate. Default value is the month.
   * @param endDate End date for the period. Required only for the custom period.
   * @param periodDateKind Period date type: paid at date or salary date.
   * @param workers List of workers (by official id) to include in the report.
   * @returns A Promise with result data (Raw data for summary reports).
   */
  public getSummaryReportData(refDate: string, periodType?: PeriodType, endDate?: string, periodDateKind?: PeriodDateKind, workers?: string[]): Promise<SummaryReportData> {
    refDate = Dates.asDate(refDate) || Dates.getToday();
    const prmsArr: string[] = [];
    if (periodType) {
      prmsArr.push(`periodType=${periodType}`);
    }
    if (endDate) {
      prmsArr.push(`endDate=${endDate}`);
    }
    if (periodDateKind) {
      prmsArr.push(`periodDateKind=${periodDateKind}`);
    }
    if (workers) {
      workers.forEach((w) => prmsArr.push(`workers=${w}`));
    }
    const prms = prmsArr.join("&");

    return this.ajax.getJSON(`/v03/api/reports/summary/${refDate}${prms ? "?" + prms : ""}`);
  }

  /**
   * Gets the monthly / quarterly/ yearly summary for worker id
   *
   * @param refDate Reference date for the period. Please note that even if the date is not the first day of the given period, the entire period is returned.
   * @param workerId For searching summary date
   * @param employerId Optionally provided and used to search workers summary date on specific employer
   * @param periodType Month, quarter, year or a custom period. The custom period requires endDate. Default value is the month.
   * @param endDate End date for the period. Required only for the custom period.
   * @param periodDateKind Period date type: paid at date or salary date.
   * @returns A Promise with result data (Raw data for summary reports).
   */
   public getWorkerSummaryReportData(refDate: string, workerId:string, employerId?:string, periodType?: PeriodType, endDate?: string, periodDateKind?: PeriodDateKind): Promise<WorkerSummaryReportData> {
    refDate = Dates.asDate(refDate) || Dates.getToday();
    const prmsArr: string[] = [];
    if (periodType) {
      prmsArr.push(`periodType=${periodType}`);
    }
    if (endDate) {
      prmsArr.push(`endDate=${endDate}`);
    }
    if (periodDateKind) {
      prmsArr.push(`periodDateKind=${periodDateKind}`);
    }
    const prms = prmsArr.join("&");

    return this.ajax.getJSON(`/v03/api/reports/summary/${refDate}/${workerId}/${employerId ? employerId : ""}${prms ? "?" + prms : ""}`);
  }

  /**
   * Gets the monthly / quarterly/ yearly summary data data based on given set of calculations.
   *
   * @param calculationIds - Calculations that are the bases for the report.
   * @returns A Promise with result data (Raw data for summary reports).
   */
  public getSummaryReportDataForCalculationIds(calculationIds: string[]): Promise<SummaryReportData> {
    const ids = calculationIds.map((x) => `calculationIds=${x}`).join("&");
    return this.ajax.getJSON(`/v03/api/reports/accounting?${ids}`);
  }

  /**
   * Returns the raw data for the calculation based reports.
   *
   * @param refDate Reference date for the period. Please note that even if the date is not the first day of the given period, the entire period is returned.
   * @param periodType Month, quarter, year or a custom period. The custom period requires endDate. Default value is the month.
   * @param endDate End date for the period. Required only for the custom period.
   * @param periodDateKind Period date type: paid at date or salary date.
   * @param workers List of workers (by official id) to include in the report.
   * @returns A Promise with result data (Raw data for the calculation based reports).
   */
  public getCalculationReportData(refDate: string, periodType?: PeriodType, endDate?: string, periodDateKind?: PeriodDateKind, workers?: string[]): Promise<CalculationReportData> {
    refDate = Dates.asDate(refDate) || Dates.getToday();
    const prmsArr: string[] = [];
    if (periodType) {
      prmsArr.push(`periodType=${periodType}`);
    }
    if (endDate) {
      prmsArr.push(`endDate=${endDate}`);
    }
    if (periodDateKind) {
      prmsArr.push(`periodDateKind=${periodDateKind}`);
    }
    if (workers) {
      workers.forEach((w) => prmsArr.push(`workers=${w}`));
    }
    const prms = prmsArr.join("&");

    return this.ajax.getJSON(`/v03/api/reports/calculation/${refDate}${prms ? "?" + prms : ""}`);
  }

  /**
   * Returns the raw data for the calculation based reports.
   *
   * @param calculationIds - Calculations that are the bases for the report.
   * @returns A Promise with result data (Raw data for the calculation based reports).
   */
  public getCalculationReportDataForCalculationIds(calculationIds: string[]): Promise<CalculationReportData> {
    const ids = calculationIds.map((x) => `calculationIds=${x}`).join("&");
    return this.ajax.getJSON(`/v03/api/reports/calculation?${ids}`);
  }

  /**
   * Returns the raw data for the accounting row based reports.
   *
   * @param refDate Reference date for the period. Please note that even if the date is not the first day of the given period, the entire period is returned.
   * @param periodType Month, quarter, year or a custom period. The custom period requires endDate. Default value is the month.
   * @param endDate End date for the period. Required only for the custom period.
   * @param periodDateKind Period date type: paid at date or salary date.
   * @param workers List of workers (by official id) to include in the report.
   * @returns A Promise with result data (Raw data for the accounting row based reports).
   */
  public getAccountingRowReportData(refDate: string, periodType?: PeriodType, endDate?: string, periodDateKind?: PeriodDateKind, workers?: string[]): Promise<AccountingRowReportData> {
    refDate = Dates.asDate(refDate) || Dates.getToday();
    const prmsArr: string[] = [];
    if (periodType) {
      prmsArr.push(`periodType=${periodType}`);
    }
    if (endDate) {
      prmsArr.push(`endDate=${endDate}`);
    }
    if (periodDateKind) {
      prmsArr.push(`periodDateKind=${periodDateKind}`);
    }
    if (workers) {
      workers.forEach((w) => prmsArr.push(`workers=${w}`));
    }
    const prms = prmsArr.join("&");

    return this.ajax.getJSON(`/v03/api/reports/accounting-row/${refDate}${prms ? "?" + prms : ""}`);
  }

  /**
   * Returns the raw data for the accounting row based reports.
   *
   * @param calculationIds - Calculations that are the bases for the report.
   * @returns A Promise with result data (Raw data for the accounting row based reports).
   */
  public getAccountingRowReportDataForCalculationIds(calculationIds: string[]): Promise<AccountingRowReportData> {
    const ids = calculationIds.map((x) => `calculationIds=${x}`).join("&");
    return this.ajax.getJSON(`/v03/api/reports/accounting-row?${ids}`);
  }

  /**
   * Returns a calculation with cumulative data for reporting purposes.
   * @param id Id of the calculation.
   * @returns Calculation with cumulative data included.
   */
  public getCumulativeCalculation(id: string): Promise<CumulativeCalculation> {
    return this.ajax.getJSON(`/v03/api/reports/cumulative-calculation/${id}`);
  }

  /**
   * Returns a URL for a pay certificate pdf file. This is a absolute link containing
   * both the server address and the access token.
   * @param workerId Worker id for which the certificate should be created.
   * @param calculationIds Calculation ids to include in the certificate.
   * @param inline - If true, the Content-Disposition header is returned with inline parameter.
   */
   public getPayCertificatePdfUrl(workerId: string, calculationIds: string[],  inline = false): string {
    const url = ReportsLogic.getPayCertificatePdfUrl(workerId, calculationIds, inline, this.ajax.getCurrentToken()!);
    return `${this.ajax.getServerAddress()}${url}`;
  }

  /**
   * Validates the pay certificate server side.
   * @param workerId Id of the worker in the certificate.
   * @param calculationIds Calculation ids to include in the certificate.
   * @returns Validation result.
   */
  public validatePayCertificate(workerId: string, calculationIds: string[]): Promise<ApiValidation> {
    let method = `/v03/api/reports/validate/pay-certificate/${encodeURIComponent(workerId)}?`;
    calculationIds.forEach((id) => { method += "calculationIds=" + encodeURIComponent("" + (id ?? "")) + "&"; });
    method = method.replace(/[?&]$/, "");
    return this.ajax.getJSON(method);
  }

  /**
   * Gets an HTML report based on Calculation ID.
   *
   * @param reportType - Type of the report to fetch. See the HtmlReportType enumeration for possible values.
   * @param calculationId - GUID for the calculation
   *
   * @returns A Promise with result HTML.
   */
  public getReportHtmlById(reportType: reportPartial, calculationId: string): Promise<string> {
    let method: string;
    if (!reportType) {
      return Promise.resolve("");
    }
    switch (reportType.toLowerCase()) {
      case "employer":
        method = "/reportpartials/employerCalculation/" + calculationId;
        break;
      case "employerpage2":
        method = "/reportpartials/employerCalculationAdditionalInfo/" + calculationId;
        break;
      case "worker":
        method = "/reportpartials/workerCalculation/" + calculationId;
        break;
      case "workerpage2":
        method = "/reportpartials/workerCalculationAdditionalInfo/" + calculationId;
        break;
      case "payments":
        method = "/reportpartials/paymentCalculation/" + calculationId;
        break;
      default:
        throw new Error("Unknown report partial type: " + reportType);
    }
    return this.ajax.getHTML(this.ajax.getServerAddress() + method);
  }

  /**
   * Gets the monthly / quarterly/ yearly accounting data for the current account.
   *
   * @deprecated Use new accounting/{target} -method for more detailed accounting data
   * @param refDate - Reference date for the period. Please note that even if the date is not the first day of the given period, the entire period is returned.
   * @param tableType  - Accounting table type.
   * @param periodType - Month, quarter, year or a custom period. The custom period requires endDate. Default value is the month.
   * @param endDate - End date for the period. Required only for the custom period.
   * @returns A Promise with result data (Raw data for accounting purposes).
   * @ignore
   */
  public getAccountingReportTableForPeriod(
    refDate: string,
    tableType: AccountingReportTableType = AccountingReportTableType.Classic,
    periodType: PeriodType = PeriodType.Month,
    endDate: string | null = null,
  ): Promise<AccountingReportTable> {
    refDate = Dates.asDate(refDate) || Dates.getToday();
    let method = "/reports/data/accounting/" + tableType + "/" + encodeURIComponent(refDate) + "?";
    method += "periodType=" + encodeURIComponent("" + periodType);
    if (endDate) {
      method += "&endDate=" + encodeURIComponent(endDate);
    }
    return this.ajax.getJSON(method);
  }

  /**
   * Experimental: Gets the accounting report based on given set of calculations.
   *
   * @deprecated Use new accounting/{target} -method for more detailed accounting data
   * @param calculationIds - Calculations that are the bases for the report.
   * @param tableType  - Accounting table type.
   * @returns Account report based on the calculations.
   * @ignore
   */
  public getAccountingReportTableForCalculationIds(calculationIds: string[], tableType: AccountingReportTableType = AccountingReportTableType.Classic): Promise<AccountingReportTable> {
    let method = "/reports/data/accounting/" + tableType + "?";
    calculationIds.forEach((x) => { method += "calculationIds=" + encodeURIComponent("" + x) + "&"; });
    method = method.replace(/[?&]$/, "");
    return this.ajax.getJSON(method);
  }

  /**
   * Experimental: Gets the accounting report based on given set of calculations.
   *
   * @deprecated Use new accounting/{target} -method for more detailed accounting data
   * @param calculations - Calculations that are the bases for the report.
   * @param tableType  - Accounting table type.
   * @returns Account report based on the calculations.
   * @ignore
   */
  public getAccountingReportTableForCalculations(calculations: Calculation[], tableType: AccountingReportTableType = AccountingReportTableType.Classic): Promise<AccountingReportTable> {
    const method = "/reports/data/accounting/" + tableType;
    return this.ajax.postJSON(method, calculations);
  }

  /**
   * Returns the  holiday pay debt report data.
   *
   * @param refDate Reference date to which the holiday pay debt is calculated.
   * @param employmentIds Optional list of employment ids (by official id) to include in the report.
   * @returns A Promise with result data containing holiday pay debt data.
   */
  public getAccountingHolidayPayDebtReportData(refDate: string, employmentIds?: string[]): Promise<AccountingHolidayPayDebtReportData> {
    refDate = Dates.asDate(refDate) || Dates.getToday();
    const prmsArr: string[] = [];
    if (employmentIds) {
      employmentIds.forEach((x) => prmsArr.push(`employmentIds=${x}`));
    }
    const prms = prmsArr.join("&");

    return this.ajax.getJSON(`/v03/api/reports/holiday-pay-debt/${refDate}${prms ? "?" + prms : ""}`);
  }

  /**
   * Returns the calculated pay statistics for the given employment relation.
   * @param employmentId The employment relation for which to calculate the statistics values.
   * @returns A Promise with result data containing pay stats calculation report data.
   */
  public getPayStatsCalculationReportData(employmentId: string): Promise<PayStatsCalculationReportData> {
    return this.ajax.getJSON(`/v03/api/reports/pay-stats-calc/${employmentId}`);
  }

  /**
   * Gets a calculation report in PDF format.
   * @param calculations - The calculations to generate the report for
   * @param reportType - The type of report to generate. Can be one of:
   * - "salarySlip" - Salary slip report
   * - "employerReport" - Employer report
   * - "paymentReport" - Payment report
   * - "paymentSummaryReport" - Payment summary report
   * - "accountingReport" - Accounting report
   * - "totalsReport" - Totals report
   * - "rowsReport" - Rows report
   * - "irRows" - IR rows report
   * @param employerLang - The language to use for employer-specific content
   * @param reportLang - The language to use for worker-specific content
   * @returns Promise resolving to an object containing:
   * - data: Blob - The PDF report data
   * - response: any - The full response object
   */
  public getCalculationReport(calculations: Calculation[], reportType: calcReportType, employerLang: Language, reportLang: Language): Promise<{data: Blob, headers: {[key: string]: string}}> {
    // Helper function to make HTTP request and return PDF data
    const makeHttpRequest = (url: string, payload?: any): Promise<{data: Blob, headers: {[key: string]: string}}> => {
     if(payload) {
      return this.ajax.postBlob(url, payload);
     } else {
      return this.ajax.getBlob(url);
     }
    };

    // Handle special report types that use the report server
    if (["paymentSummaryReport", "accountingReport", "totalsReport", "rowsReport"].includes(reportType)) {
      const baseUrl = `${Configs.current.reportServer}/pdf`;
      const commonParams = `disposition=inline&access_token=${this.ajax.getCurrentToken()}`;

      switch (reportType) {
        case ReportType.PaymentSummaryReport: {
          const url = `${baseUrl}?id=&reportType=${encodeURIComponent(reportType)}&template=paymentSummaryReportV2&language=${encodeURIComponent(employerLang)}&${commonParams}`;
          return makeHttpRequest(url, { object: calculations });
        }

        case ReportType.AccountingReport: {
          const url = `${baseUrl}?id=&reportType=${encodeURIComponent(reportType)}&template=accountingReport&language=${encodeURIComponent(employerLang)}&${commonParams}`;
          return this.getAccountingDataForCalculations(calculations).then(accountingData => makeHttpRequest(url, { object: accountingData }));
        }

        case ReportType.TotalsReport: {
          const url = `${baseUrl}?id=&reportType=${encodeURIComponent(reportType)}&template=totalsReport&language=${encodeURIComponent(employerLang)}&${commonParams}`;
          return makeHttpRequest(url, { object: calculations });
        }

        default:
          throw new Error(`Report type ${reportType} is not supported`);
      }
    }

    // Handle standard report types
    const baseUrl = `${this.ajax.getServerAddress()}/v03/api/reports/assure-pdf/${reportType}`;
    const allCalculationsSucceeded = calculations.every(calc => calc?.workflow?.status === CalculationStatus.PaymentSucceeded);

    if (allCalculationsSucceeded) {
      const ids = calculations.map(calc => `ids=${encodeURIComponent(calc.id!)}`).join("&");
      const url = `${baseUrl}?disposition=inline&${ids}&language=${encodeURIComponent(reportLang)}&access_token=${this.ajax.getCurrentToken()}`;
      return makeHttpRequest(url);
    } else {
      const url = `${baseUrl}?language=${encodeURIComponent(reportLang)}&disposition=inline&access_token=${this.ajax.getCurrentToken()}`;
      return makeHttpRequest(url, calculations);
    }
  }
}
